import { useState, useContext, useEffect } from 'react';
import { cn } from '../../utils/shadcn';
import { Skeleton } from '../shadcn/ui/skeleton';
import { flexRender, getCoreRowModel, getSortedRowModel, Table, useReactTable } from '@tanstack/react-table';
import { VariableSizeList as List } from 'react-window';
import { Spinner } from '../core/Spinner';
import { usePaginationRequest } from '../../hooks/usePaginationRequest';
import { getMyTwapOrders, cancelTwapOrder } from 'deso-protocol';
import { OpenfundContext } from '../../contexts/OpenfundContext';
import { TwapOrderResponse } from 'deso-protocol/src/transactions/amm';
import { twapColumns, TwapStatus } from './TWAPOrdersTableColumns';
import { TableItem, TableParams } from './ProjectHoldersTable';
import { Button } from '../shadcn/ui/button';
import { LuRefreshCcw } from 'react-icons/lu';
import { useToast } from '../../hooks/useToast';
import { getErrorMsg } from '../../utils/getErrorMsg';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../shadcn/ui/select';
import { Label } from '../shadcn/ui/label';
import capitalize from 'lodash/capitalize';
import { DESO_ZERO_PUBLIC_KEY } from '../../constants/AppConstants';
import { TableLoader } from './GenericTable';

interface TWAPOrdersTableProps {
  className?: string;
  height?: number;
  baseCurrencyPublicKey: string;
}

const ITEMS_PER_PAGE = 10;

export const TWAPOrdersTable = ({ className, height = 500, baseCurrencyPublicKey }: TWAPOrdersTableProps) => {
  const { currentUser } = useContext(OpenfundContext);

  const toast = useToast();

  const [orders, setOrders] = useState<TwapOrderResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skipPagination, setSkipPagination] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState<null | number>(null);
  const [selectedMarket, setSelectedMarket] = useState<string>('All Orders');
  const [selectedStatus, setSelectedStatus] = useState<TwapStatus | 'All'>('All');

  const getOrders = async (updatedOrders: TwapOrderResponse[] = orders) => {
    try {
      const response = await getMyTwapOrders({
        ...{
          publicKey: currentUser?.PublicKeyBase58Check || '',
          limit: ITEMS_PER_PAGE,
          offset: updatedOrders.length,
        },
        ...(selectedMarket === 'Current Market' && {
          baseCurrencyPubKey: baseCurrencyPublicKey === DESO_ZERO_PUBLIC_KEY ? 'DESO' : baseCurrencyPublicKey,
        }),
        ...(selectedStatus !== 'All' && {
          status: selectedStatus,
        }),
      });

      const fetchedOrders = response.TwapOrders || [];
      setTotalCount(response.TotalCount || 0);
      setOrders([...updatedOrders, ...fetchedOrders]);
    } catch (error) {
      toast.show({ message: getErrorMsg(error), type: 'error' });
    }
  };

  const { ref, loadingMore, initialLoading } = usePaginationRequest({
    initialRequest: getOrders,
    fetchRequest: getOrders,
    hasMoreItems: totalCount > orders.length,
    skipRequest: skipPagination,
  });

  const COLUMNS_WIDTH = [6, 12, 9, 12, 13, 15, 15, 13, 8];

  const table = useReactTable({
    data: orders,
    columns: twapColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    meta: {
      onCancel: async (configId: number) => {
        setCancellingOrderId(configId);

        await cancelTwapOrder({
          OwnerPublicKey: currentUser?.PublicKeyBase58Check || '',
          TwapConfigId: configId,
        });

        await refresh();

        setCancellingOrderId(null);
      },
      cancellingOrderId,
    },
  });

  const refresh = async () => {
    setTotalCount(0);
    setSkipPagination(true);
    setRefreshing(true);

    try {
      await getOrders([]);
    } finally {
      setSkipPagination(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!initialLoading) {
      refresh();
    }
  }, [selectedMarket, selectedStatus]);

  const tableRows = table.getRowModel().rows;

  return (
    <>
      <div className={cn('w-full text-muted')}>
        {initialLoading ? (
          <div className="pb-2">
            <Skeleton className="h-[40px] w-full" />
            <Skeleton className="mt-4 h-[280px] w-full" />
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row items-start justify-between md:items-center mb-4">
              <div className="flex gap-2 items-center font-sans text-xl text-muted-foreground font-semibold">
                <div className="flex justify-between items-center lg:items-end">My TWAP Orders</div>
                <Button
                  variant="link"
                  className="flex items-center gap-2 py-0 text-muted underline-offset-2 hover:underline"
                  size="sm"
                  disabled={refreshing}
                  onClick={() => {
                    refresh();
                  }}
                >
                  <LuRefreshCcw /> Refresh
                </Button>
              </div>

              <div className="flex gap-x-4">
                <div>
                  <Label className="text-xs" htmlFor="market-select">
                    Show
                    <Select
                      value={selectedMarket?.toString()}
                      onValueChange={(value: string) => setSelectedMarket(value)}
                    >
                      <SelectTrigger className="w-40" id="market-select">
                        <SelectValue placeholder="Select Market" />
                      </SelectTrigger>
                      <SelectContent>
                        {['All Orders', 'Current Market'].map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Label>
                </div>

                <div>
                  <Label className="text-xs" htmlFor="status-select">
                    Status
                    <Select
                      value={selectedStatus?.toString()}
                      onValueChange={(value) => setSelectedStatus(value as TwapStatus)}
                    >
                      <SelectTrigger className="w-40" id="status-select">
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {['All', ...Object.values(TwapStatus)].map((status) => (
                          <SelectItem key={status} value={status}>
                            {capitalize(status.replace('_', ' '))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Label>
                </div>
              </div>
            </div>

            <div className="w-full overflow-auto border border-border rounded-lg">
              {table.getHeaderGroups().map((headerGroup) => (
                <div key={headerGroup.id} className="flex w-full min-w-[1200px] border-b">
                  {headerGroup.headers.map((header, idx) => (
                    <div
                      className="flex h-11 items-center px-2 text-left text-xs font-medium text-muted-foreground last:justify-end last:pr-4"
                      key={header.id}
                      style={{ width: `${COLUMNS_WIDTH[idx]}%` }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  ))}
                </div>
              ))}

              <div className="relative h-1 min-w-[1200px]">{refreshing && <TableLoader />}</div>

              {tableRows?.length ? (
                <List
                  width="100%"
                  height={height}
                  itemCount={tableRows.length}
                  itemSize={() => 52}
                  itemData={{
                    table: table as unknown as Table<TableParams>,
                    tableRows,
                    columnsWidth: COLUMNS_WIDTH,
                    renderLoader: () =>
                      ((!initialLoading && orders.length !== totalCount) || loadingMore) && (
                        <div key="load-more-row" ref={ref}>
                          <div className="flex items-center justify-center gap-x-2 py-2">
                            <Spinner size={20} />
                            <span>Loading...</span>
                          </div>
                        </div>
                      ),
                  }}
                  style={{
                    minWidth: 1200,
                  }}
                >
                  {TableItem<TwapOrderResponse>}
                </List>
              ) : (
                <div className="h-24 text-center flex items-center justify-center">
                  {refreshing ? 'Loading...' : 'No TWAP orders found.'}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TWAPOrdersTable;
